import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import Spinner            from "react-bootstrap/Spinner";
import { toast }          from "react-toastify";
import moment             from "moment";
import WalletService      from "services/wallet.service";
import PayoutMethodsService from "services/payout-methods.service";
import AuthService        from "services/auth.service";
import { formatCurrency } from "helpers/common.helper";
import WhiteCheck         from "assets/icons/white-check.svg";
import WalletPasscodeService from "services/wallet-passcode.service";
import AddAccountModal    from "./AddAccountModal";
import AccountSavedModal  from "./AccountSavedModal";
import WithdrawSuccessModal from "./WithdrawSuccessModal";
import PasscodeGateModal  from "./PasscodeGateModal";
import PasscodeSetupModal from "./PasscodeSetupModal";
import "./Withdraw.scss";

const maskedNumber = (pm) => {
  const raw    = pm.account_number || pm.account_last_four || "";
  const digits = String(raw).replace(/\D/g, "");
  const last4  = digits.slice(-4);
  return last4 ? `**** **** ${last4}` : "";
};

const accountTitle = (pm) => {
  const name = pm.description || pm.bank_name || "Account";
  return pm.is_default ? `${name} (Default)` : name;
};

const Withdraw = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep]                   = useState("details");
  const [balance, setBalance]             = useState(location.state?.balance ?? null);
  const [payoutMethods, setPayoutMethods] = useState([]);
  const [payoutMethodId, setPayoutMethodId] = useState(null);
  const [loader, setLoader]               = useState(true);
  const [submitting, setSubmitting]       = useState(false);
  const [showAddModal, setShowAddModal]   = useState(false);
  const [showSavedModal, setShowSavedModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData]     = useState(null);

  const [gate, setGate]         = useState("checking"); // checking|setup|entry|locked|unlocked
  const [gateInfo, setGateInfo] = useState({});

  useEffect(() => {
    loadBalance();   // ungated endpoint - safe to fire immediately
    resolveGate();
    return () => {};
  }, []); // eslint-disable-line

  useEffect(() => {
    if (gate === "unlocked") loadPayoutMethods();
  }, [gate]); // eslint-disable-line

  useEffect(() => {
    if (gate !== "unlocked") return undefined;

    const msLeft = WalletPasscodeService.expiresAt() - Date.now();

    if (msLeft <= 0) {
      setGate("entry");
      return undefined;
    }

    const timer = setTimeout(() => {
      WalletPasscodeService.clearUnlock();
      setGate("entry");
      toast.info("Your wallet passcode session expired. Please enter it again.");
    }, msLeft);

    return () => clearTimeout(timer);
  }, [gate]); // eslint-disable-line

  function handleError(error) {
    if (error.response && error.response.status === 401) {
      AuthService.logout();
      window.location.replace("/eb/login");
    }
  }

  function handleGateError(error) {
    const code = WalletPasscodeService.gateCode(error);
    if (!code) return false;

    WalletPasscodeService.clearUnlock();
    setGateInfo(error.response.data.wallet_passcode || {});

    if (code === "wallet_passcode_locked")        setGate("locked");
    else if (code === "wallet_passcode_not_set")  setGate("setup");
    else                                          setGate("entry");

    return true;
  }

  function resolveGate() {
    if (WalletPasscodeService.isUnlocked()) {
      setGate("unlocked");
      return;
    }

    WalletPasscodeService.status().then(
      (data) => {
        const info = data.wallet_passcode || {};
        setGateInfo(info);
        setGate(info.locked ? "locked" : (info.set ? "entry" : "setup"));
      },
      (error) => {
        handleError(error);
        setLoader(false);
      }
    );
  }

  function loadBalance() {
    if (balance != null) return;   // seeded from location.state by the wallet page

    WalletService.transactions("page=1&per=1").then(
      (data) => setBalance(data.metadata ? data.metadata.actual_withdrawal_balance : 0),
      (error) => handleError(error)
    );
  }

  function loadPayoutMethods() {
    setLoader(true);

    PayoutMethodsService.list().then(
      (data) => applyPayoutMethods(data.payout_methods || []),
      (error) => { if (!handleGateError(error)) handleError(error); }
    ).then(() => setLoader(false));
  }

  function applyPayoutMethods(methods, preferId) {
    setPayoutMethods(methods);

    setPayoutMethodId((current) => {
      if (preferId && methods.some((m) => m.id === preferId)) return preferId;
      if (current && methods.some((m) => m.id === current)) return current;
      const defaultMethod = methods.find((m) => m.is_default);
      return defaultMethod ? defaultMethod.id : (methods[0] ? methods[0].id : null);
    });
  }

  const handleAccountCreated = (methods) => {
    const newest = methods.find((m) => m.is_default) || methods[methods.length - 1];
    applyPayoutMethods(methods, newest ? newest.id : null);
    setShowAddModal(false);
    setShowSavedModal(true);
  };

  const handleConfirm = () => {
    setSubmitting(true);

    WalletService.withdraw({ payout_method_id: payoutMethodId, amount: balance }).then(
      (data) => {
        if (data.success) {
          const tx = data.transaction || {};
          setSuccessData({
            amount:      `RM ${formatCurrency(tx.amount != null ? tx.amount : balance)}`,
            accountNo:   selectedMethod ? maskedNumber(selectedMethod) : "",
            bankAccount: selectedMethod ? selectedMethod.bank_name : "",
            dateTime:    moment(tx.created_at || undefined).format("DD MMM YYYY, hh:mm A"),
            referenceNo: tx.reference_no || "",
          });
          setShowSuccessModal(true);
        } else {
          toast.error(data.error || "Failed to process withdrawal");
        }
      },
      (error) => {
        if (error.response && error.response.status === 401) {
          AuthService.logout();
          window.location.replace("/eb/login");
        } else if (handleGateError(error)) {
          setStep("summary");
        } else if (error.response && error.response.status === 409) {
          toast.error(error.response.data?.error || "Your balance has updated, please refresh.");
        } else {
          toast.error(error.response?.data?.error || "Failed to process withdrawal");
        }
      }
    ).then(() => {
      setSubmitting(false);
    });
  };

  const selectedMethod = payoutMethods.find((m) => m.id === payoutMethodId);
  const canContinue    = !!payoutMethodId && balance > 0;

  const renderStepper = () => {
    const summaryActive = step === "summary";
    return (
      <div className="withdraw-stepper">
        <div className={ `step ${summaryActive ? "done" : "active"}` }>
          <div className="step-circle">
            { summaryActive ? <img src={ WhiteCheck } alt="done" /> : <span className="step-dot" /> }
          </div>
          <span className="step-label">Details</span>
        </div>

        <div className={ `step-line ${summaryActive ? "filled" : ""}` } />

        <div className={ `step ${summaryActive ? "active" : ""}` }>
          <div className="step-circle">
            { summaryActive && <span className="step-dot" /> }
          </div>
          <span className="step-label">Summary</span>
        </div>
      </div>
    );
  };

  const renderDetails = () => (
    <>
      <h2 className="withdraw-title">Withdraw Details</h2>

      <div className="field-row">
        <div className="field-label">Amount*</div>
        <div className="field-control">
          <div className="amount-box">
            <span className="amount-currency">RM</span>
            <input className="amount-value" value={ formatCurrency(balance) } readOnly />
            <span className="amount-balance">Wallet balance: <b>RM { formatCurrency(balance) }</b></span>
          </div>
        </div>
      </div>

      <div className="field-row">
        <div className="field-label">Withdraw to*</div>
        <div className="field-control">
          <div className="account-list">
            { payoutMethods.map((pm) => (
              <div
                key={ pm.id }
                className={ `account-option ${payoutMethodId === pm.id ? "selected" : ""}` }
                onClick={ () => setPayoutMethodId(pm.id) }
              >
                <span className="account-radio" />
                <div className="account-info">
                  <div className="account-name">{ accountTitle(pm) }</div>
                  <div className="account-number">{ maskedNumber(pm) }</div>
                </div>
              </div>
            )) }

            <button type="button" className="add-account" onClick={ () => setShowAddModal(true) }>
              <span className="add-icon">+</span> Add new account
            </button>
          </div>
        </div>
      </div>

      <div className="withdraw-actions">
        <button type="button" className="btn btn-coral-outline" onClick={ () => navigate("/eb/wallet") }>Back</button>
        <button type="button" className="btn btn-coral" disabled={ !canContinue } onClick={ () => setStep("summary") }>Continue</button>
      </div>
    </>
  );

  const renderSummary = () => (
    <>
      <h2 className="withdraw-title">Summary</h2>

      <div className="summary-row">
        <span className="summary-label">Amount</span>
        <span className="summary-value">RM { formatCurrency(balance) }</span>
      </div>
      <div className="summary-row">
        <span className="summary-label">Account No.</span>
        <span className="summary-value">{ selectedMethod ? maskedNumber(selectedMethod) : "-" }</span>
      </div>
      <div className="summary-row">
        <span className="summary-label">Account Holder Name</span>
        <span className="summary-value">{ selectedMethod ? selectedMethod.account_holder_name : "-" }</span>
      </div>
      <div className="summary-row">
        <span className="summary-label">Bank Account</span>
        <span className="summary-value">{ selectedMethod ? selectedMethod.bank_name : "-" }</span>
      </div>

      <p className="summary-disclaimer">
        Incorrect details may lead to declined transactions, and any processing fees incurred will be your responsibility.
      </p>

      <div className="withdraw-actions">
        <button type="button" className="btn btn-coral-outline" onClick={ () => setStep("details") }>Back</button>
        <button type="button" className="btn btn-coral" disabled={ submitting } onClick={ handleConfirm }>Confirm and Proceed</button>
      </div>
    </>
  );

  return (
    <div id="main-content-target" className="wallet-withdraw-page">
      <div className="withdraw-breadcrumb">
        <NavLink to="/eb/wallet">My Wallet</NavLink>
        <span className="crumb-sep">&gt;</span>
        <span className="crumb-current">Withdraw</span>
      </div>

      { renderStepper() }

      { gate !== "unlocked"
        ? <div className="withdraw-body withdraw-locked">
            <p className="locked-hint">Your wallet is protected by a 6-digit passcode.</p>
          </div>
        : loader
          ? <div className="loader"><Spinner animation="border" className="spinner" /></div>
          : <div className="withdraw-body">{ step === "details" ? renderDetails() : renderSummary() }</div>
      }

      <PasscodeSetupModal
        show={ gate === "setup" }
        onDone={ () => setGate("unlocked") }
        onCancel={ () => navigate("/eb/wallet") }
      />

      <PasscodeGateModal
        show={ gate === "entry" || gate === "locked" }
        locked={ gate === "locked" }
        retryIn={ gateInfo.retry_in }
        onUnlocked={ () => setGate("unlocked") }
        onNeedsSetup={ () => setGate("setup") }
        onCancel={ () => navigate("/eb/wallet") }
      />

      <AddAccountModal
        show={ showAddModal }
        onClose={ () => setShowAddModal(false) }
        onCreated={ handleAccountCreated }
        onGateError={ handleGateError }
      />

      <AccountSavedModal
        show={ showSavedModal }
        onClose={ () => setShowSavedModal(false) }
      />

      { successData &&
        <WithdrawSuccessModal
          show={ showSuccessModal }
          data={ successData }
          onDone={ () => { setShowSuccessModal(false); navigate("/eb/wallet"); } }
        />
      }
    </div>
  );
};

export default Withdraw;
